import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useAuth, API } from '../App';
import { Button } from '../components/ui/button';

const PaymentSuccessPage = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('loading');
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');
  const isMock = searchParams.get('mock') === 'true';
  const provider = searchParams.get('provider'); // 'revolut' or null for stripe/mock
  const revolutTxId = searchParams.get('tx_id');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      return;
    }
    if (provider !== 'revolut' && !sessionId) {
      setStatus('error');
      return;
    }
    if (provider === 'revolut' && !revolutTxId) {
      setStatus('error');
      return;
    }

    const pollStatus = async () => {
      try {
        const headers = { Authorization: `Bearer ${token}` };
        const url =
          provider === 'revolut'
            ? `${API}/checkout/revolut/status/${revolutTxId}`
            : `${API}/checkout/status/${sessionId}`;
        const res = await axios.get(url, { headers });

        if (res.data.payment_status === 'paid') {
          setStatus('success');
          return;
        } else if (res.data.status === 'expired' || res.data.payment_status === 'failed') {
          setStatus('error');
          return;
        }

        // Continue polling
        if (attempts < 5) {
          setTimeout(() => setAttempts((a) => a + 1), 2000);
        } else {
          setStatus('error');
        }
      } catch (e) {
        console.error('Status check failed', e);
        setStatus('error');
      }
    };

    pollStatus();
  }, [sessionId, token, attempts, provider, revolutTxId]);

  return (
    <div className="min-h-screen bg-misel-bg flex items-center justify-center p-8">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
        className="text-center max-w-md"
      >
        {status === 'loading' && (
          <>
            <Loader2 size={64} className="text-primary mx-auto mb-6 animate-spin" />
            <h1 className="font-roboto font-bold text-2xl text-white mb-4">
              {t('payment.processing')}
            </h1>
          </>
        )}

        {status === 'success' && (
          <>
            <CheckCircle size={64} className="text-green-500 mx-auto mb-6" />
            <h1 className="font-roboto font-bold text-2xl text-white mb-4">
              {t('payment.success')}
            </h1>
            <p className="text-gray-400 mb-8">
              {i18n.language === 'el'
                ? 'Η πληρωμή σας ολοκληρώθηκε με επιτυχία. Μπορείτε τώρα να παρακολουθήσετε το περιεχόμενο.'
                : 'Your payment was successful. You can now watch the content.'
              }
            </p>
            <div className="flex flex-col gap-4">
              <Link to="/categories">
                <Button className="w-full bg-primary hover:bg-primary/90">
                  Browse Categories
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full border-white/20 text-white hover:bg-white/10">
                  {t('payment.back_home')}
                </Button>
              </Link>
            </div>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-6">
              <span className="text-red-500 text-3xl">!</span>
            </div>
            <h1 className="font-roboto font-bold text-2xl text-white mb-4">
              {t('payment.failed')}
            </h1>
            <p className="text-gray-400 mb-8">
              {i18n.language === 'el'
                ? 'Η πληρωμή απέτυχε ή έχει λήξει. Παρακαλώ δοκιμάστε ξανά.'
                : 'The payment failed or expired. Please try again.'
              }
            </p>
            <Link to="/pricing">
              <Button className="w-full bg-primary hover:bg-primary/90">
                {i18n.language === 'el' ? 'Δοκιμάστε ξανά' : 'Try Again'}
              </Button>
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default PaymentSuccessPage;
